/**
 * D-1028-FORMS part 2 — a waiver on screen the way it is on paper. The words are the app's stored form text (shared/documents.ts,
 * the very string that is hashed and signed), never retyped; this only lays them out like the Director's PDF: the logo, the
 * title block, the numbered headings, the (a) (b) (c) lines indented under their paragraph, and at the end the signature
 * lines. Real headings, real lists, readable at large text, and the same words in the same order for a screen reader.
 * Spanish text gets the same layout.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type PaperKind = 'companion-waiver' | 'volunteer-waiver';

export interface PaperSignature {
  /** The typed name, as it is typed — shown on the signature line like handwriting (decorative; the field itself is the input). */
  typed: string;
  /** The name printed under it. */
  printed?: string;
  /** The date shown on the Date line. */
  date: string;
}

const inline = (s: string): ReactNode[] => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <b key={i}>{part.slice(2, -2)}</b> : part));

/** "Program — For participants. In partnership with …. Address. Updated …." → its five lines, or null when the cover is not that shape. */
export function coverLines(p: string): { program: string; tagline?: string; eyebrow: string; lines: string[]; updated: string } | null {
  // the Director's PDF order, one line each: program, tagline, FOR PROGRAM PARTICIPANTS, partnership, address, updated
  const rows = p.split('\n').map((x) => x.trim()).filter(Boolean);
  if (rows.length === 6) return { program: rows[0]!, tagline: rows[1]!, eyebrow: rows[2]!, lines: [rows[3]!, rows[4]!], updated: rows[5]! };
  const [program, rest] = p.split(' — ');
  if (!program || !rest) return null;
  const parts = rest.split(/\.\s+/).map((x) => x.replace(/\.$/, '').trim()).filter(Boolean);
  if (parts.length !== 4) return null;
  return { program, eyebrow: parts[0]!, lines: [parts[1]!, parts[2]!], updated: parts[3]! };
}

/** Today as the paper's Date line is written (MM/DD/YYYY, New York). */
export function paperDate(now: Date = new Date()): string {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const g = (k: string) => p.find((x) => x.type === k)!.value;
  return `${g('month')}/${g('day')}/${g('year')}`;
}

const SUB = /^\(([a-z])\)\s+([\s\S]*)$/;

export function PaperText({ kind, text, headingLevel = 3, signature }: { kind: PaperKind; text: string; headingLevel?: 2 | 3; signature?: PaperSignature }) {
  const { t } = useTranslation();
  const H1 = headingLevel === 2 ? 'h2' : 'h3';
  const H2 = headingLevel === 2 ? 'h3' : 'h4';
  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const out: ReactNode[] = [];
  let sub: { letter: string; body: string }[] = [];
  let coverDone = false;
  const flush = (key: string) => {
    if (!sub.length) return;
    out.push(
      <ol className="paper-sub" key={key}>
        {sub.map((s) => (
          <li key={s.letter}>
            <b>({s.letter})</b> {inline(s.body)}
          </li>
        ))}
      </ol>,
    );
    sub = [];
  };
  blocks.forEach((b, i) => {
    const m = SUB.exec(b);
    if (m && !b.includes('\n')) {
      sub.push({ letter: m[1]!, body: m[2]! });
      return;
    }
    flush(`s${i}`);
    if (b.startsWith('# ')) {
      out.push(
        kind === 'companion-waiver' ? (
          <div className="paper-top" key={i}>
            <img className="paper-logo" src="/brand/logo-light.png" alt="Associated Blind Foundation" />
            <H1 className="paper-title">{b.slice(2)}</H1>
          </div>
        ) : (
          <div className="paper-top" key={i}>
            <img className="paper-logo" src="/brand/logo-light.png" alt="" />
            <H1 className="paper-org">{b.slice(2)}</H1>
          </div>
        ),
      );
    } else if (b.startsWith('## ')) {
      out.push(
        <H2 key={i} className={kind === 'volunteer-waiver' ? 'paper-title' : 'paper-h'}>
          {b.slice(3)}
        </H2>,
      );
    } else if (kind === 'companion-waiver' && !coverDone && coverLines(b)) {
      coverDone = true;
      const c = coverLines(b)!;
      out.push(
        <div className="paper-cover" key={i}>
          <p className="paper-program">{c.program}</p>
          {c.tagline ? <p className="paper-tagline">{c.tagline}</p> : null}
          <p className="paper-eyebrow">{c.eyebrow}</p>
          {c.lines.map((l) => (
            <p key={l} className="paper-line">
              {l}
            </p>
          ))}
          <p className="paper-updated">{c.updated}</p>
        </div>,
      );
    } else if (/^- /m.test(b) && b.split('\n').every((l) => l.startsWith('- '))) {
      out.push(
        <ul key={i}>
          {b.split('\n').map((l, j) => (
            <li key={j}>{inline(l.slice(2))}</li>
          ))}
        </ul>,
      );
    } else if (b.split('\n').every((l) => /^\d+\. /.test(l))) {
      out.push(
        <ol key={i}>
          {b.split('\n').map((l, j) => (
            <li key={j}>{inline(l.replace(/^\d+\. /, ''))}</li>
          ))}
        </ol>,
      );
    } else {
      out.push(
        <p key={i} className={kind === 'volunteer-waiver' ? 'paper-body' : undefined}>
          {b.split('\n').map((l, j, all) => (
            <span key={j}>
              {inline(l)}
              {j < all.length - 1 ? <br /> : null}
            </span>
          ))}
        </p>,
      );
    }
  });
  flush('end');
  return (
    <div className={`paper paper-${kind}`} data-testid={`paper-${kind}`}>
      {out}
      {signature ? <PaperLines kind={kind} signature={signature} t={t} /> : null}
    </div>
  );
}

/** The signature lines of the paper form, with what is typed showing on them the way a pen would. */
function PaperLines({ kind, signature, t }: { kind: PaperKind; signature: PaperSignature; t: (k: string) => string }) {
  const hand = signature.typed.trim();
  const printed = (signature.printed ?? '').trim();
  if (kind === 'volunteer-waiver') {
    return (
      <div className="paper-lines" data-testid="paper-lines">
        <p className="sr-only" data-testid="paper-lines-note">{t('paper1028.linesNote')}</p>
        <p className="paper-field">
          <span className="paper-label">{t('paper1028.volSignature')}</span>
          <span className="paper-rule paper-hand" aria-hidden="true">{hand}</span>
        </p>
        <p className="paper-field">
          <span className="paper-label">{t('paper1028.volPrinted')}</span>
          <span className="paper-rule" aria-hidden="true">{printed}</span>
        </p>
        <p className="paper-field short">
          <span className="paper-label">{t('paper1028.volDate')}</span>
          <span className="paper-rule" aria-hidden="true">{signature.date}</span>
        </p>
      </div>
    );
  }
  return (
    <div className="paper-lines" data-testid="paper-lines">
      <p className="sr-only" data-testid="paper-lines-note">{t('paper1028.linesNote')}</p>
      <p className="paper-who">{t('paper1028.tenant')}</p>
      <div className="paper-row">
        <p className="paper-field">
          <span className="paper-small">{t('paper1028.tenantSignature')}</span>
          <span className="paper-rule paper-hand" aria-hidden="true">{hand}</span>
        </p>
        <p className="paper-field">
          <span className="paper-small">{t('paper1028.date')}</span>
          <span className="paper-rule" aria-hidden="true">{signature.date}</span>
        </p>
      </div>
      <p className="paper-field">
        <span className="paper-small">{t('paper1028.printedName')}</span>
        <span className="paper-rule" aria-hidden="true">{printed}</span>
      </p>
      <p className="paper-who">Associated Blind Foundation, Inc.</p>
      <p className="paper-field by">
        <span className="paper-small">{t('paper1028.by')}</span>
        <span className="paper-rule" aria-hidden="true" />
        <span className="paper-small">{t('paper1028.authorized')}</span>
      </p>
    </div>
  );
}
